import { readonly, ref } from 'vue';
import axios from 'axios';
import { BACKEND_BASE_URL } from '@/config/backend';
import { getStoredToken } from '@/config/schools';

export const facultyApi = axios.create({ baseURL: BACKEND_BASE_URL });

facultyApi.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const emptyCapabilities = () => ({
  isFaculty: false,
  schoolEnabled: false,
  canWrite: false,
  excuseTypes: [],
  loaded: false,
});

const facultyCapabilities = ref(emptyCapabilities());
let capabilitiesPromise = null;
let capabilitiesPromiseToken = null;

export function fetchFacultyCapabilities({ force = false } = {}) {
  const token = getStoredToken();
  if (!token) {
    facultyCapabilities.value = { ...emptyCapabilities(), loaded: true };
    capabilitiesPromise = null;
    capabilitiesPromiseToken = null;
    return Promise.resolve(facultyCapabilities.value);
  }

  if (!force && capabilitiesPromise && capabilitiesPromiseToken === token) {
    return capabilitiesPromise;
  }

  capabilitiesPromiseToken = token;
  capabilitiesPromise = facultyApi.get('/faculty/capabilities')
    .then(({ data }) => {
      facultyCapabilities.value = {
        isFaculty: data?.isFaculty === true,
        schoolEnabled: data?.schoolEnabled === true,
        canWrite: data?.canWrite === true,
        excuseTypes: Array.isArray(data?.excuseTypes) ? data.excuseTypes : [],
        loaded: true,
      };
      return facultyCapabilities.value;
    })
    .catch((error) => {
      console.error('Failed to fetch faculty capabilities:', error);
      facultyCapabilities.value = { ...emptyCapabilities(), loaded: true };
      capabilitiesPromise = null;
      capabilitiesPromiseToken = null;
      return facultyCapabilities.value;
    });

  return capabilitiesPromise;
}

export function markFacultyReadOnly() {
  facultyCapabilities.value = {
    ...facultyCapabilities.value,
    canWrite: false,
    loaded: true,
  };
  capabilitiesPromise = Promise.resolve(facultyCapabilities.value);
}

export function useFacultyContext() {
  return {
    facultyCapabilities: readonly(facultyCapabilities),
    fetchFacultyCapabilities,
  };
}
